import { Component, Inject, OnInit, signal } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogActions } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import {
    deliveryNoteNumberValidator,
    glnValidator,
    orderNumberValidator,
    originalConsignorValidator,
    timeFormatValidator,
} from '@app/shared/form-validators';
import {
    convertDateTimeToTimestamp,
    convertTimestampToDateTime,
} from '@app/shared/date-time-utils';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeliveryNoteInPool } from 'api/models';
import { DeliveryNotesService } from 'api/services';
import { lastValueFrom } from 'rxjs';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-edit-note-dialog',
    templateUrl: './edit-note-dialog.component.html',
    styleUrls: ['./edit-note-dialog.component.scss'],
    imports: [
        MatDialogTitle,
        MatIconButton,
        MatDialogClose,
        MatIcon,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatSuffix,
        MatDatepicker,
        MatDialogActions,
        MatButton,
        TranslateModule,
    ],
})
export class EditNoteDialogComponent implements OnInit {
    loading = signal(false);
    patchDeliveryNoteForm: UntypedFormGroup;
    minDate: Date = new Date(
        new Date().setFullYear(new Date().getFullYear() - 10)
    );
    touchedDate = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: { note: DeliveryNoteInPool; organizationSiteKey: string },
        private deliveryNoteService: DeliveryNotesService,
        private toastr: ToastService,
        private dialogRef: MatDialogRef<EditNoteDialogComponent>,
        private translate: TranslateService,
        private formBuilder: UntypedFormBuilder
    ) {}

    ngOnInit(): void {
        // Parse existing timestamp to extract date and time
        const dateTimeInfo = convertTimestampToDateTime(
            this.data.note.dateAnnounced
        );

        this.patchDeliveryNoteForm = this.formBuilder.group({
            fileName: [this.data.note.fileName],
            number: [
                this.data.note.number,
                [
                    Validators.required,
                    deliveryNoteNumberValidator(),
                    Validators.minLength(3),
                    Validators.maxLength(60),
                ],
            ],
            dateAnnounced: [dateTimeInfo.date],
            timeAnnounced: [dateTimeInfo.time || '', [timeFormatValidator()]],
            orderNumber: [
                this.data.note.orderNumber || '',
                [
                    orderNumberValidator(),
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(60),
                ],
            ],
            originalConsignor: [
                this.data.note.originalConsignor || '',
                [originalConsignorValidator()],
            ],
            originalConsignorGln: [
                this.data.note.originalConsignorGln,
                [glnValidator()],
            ],
            referenceNumber: [
                this.data.note.properties?.referenceNumber || '',
                [originalConsignorValidator()],
            ],
        });
    }

    async saveChanges(): Promise<void> {
        if (this.patchDeliveryNoteForm.invalid) {
            this.toastr.warning(this.translate.instant('SHARED.FORM-INVALID'));
            this.patchDeliveryNoteForm.markAllAsTouched();
            return;
        }

        this.loading.set(true);
        const formValue = this.patchDeliveryNoteForm.value;

        // Set showDateAnnouncedTime to true if time was explicitly provided
        const hasTimeProvided = !!(
            formValue.timeAnnounced && formValue.timeAnnounced.trim() !== ''
        );

        // Convert date and time to timestamp using Europe/Berlin timezone
        const dateAnnouncedTimestamp = convertDateTimeToTimestamp(
            formValue.dateAnnounced,
            formValue.timeAnnounced
        );

        const requestBody = {
            fileName: formValue.fileName,
            number: formValue.number,
            orderNumber: formValue.orderNumber,
            originalConsignor: formValue.originalConsignor,
            originalConsignorGln: formValue.originalConsignorGln,
            dateAnnounced: dateAnnouncedTimestamp,
            showDateAnnouncedTime: hasTimeProvided,
            properties: {
                referenceNumber: formValue.referenceNumber || '',
            },
        };

        try {
            await lastValueFrom(
                this.deliveryNoteService.patchDeliveryNoteMetadata({
                    organizationSiteKey: this.data.organizationSiteKey,
                    deliveryNoteKey: this.data.note._key,
                    body: requestBody,
                })
            );
            this.toastr.success(
                this.translate.instant(
                    'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.NOTE-UPDATED'
                )
            );

            const updatedNote = this.data.note;
            updatedNote.fileName = formValue.fileName;
            updatedNote.number = formValue.number;
            updatedNote.dateAnnounced = formValue.dateAnnounced;
            updatedNote.orderNumber = formValue.orderNumber;
            updatedNote.originalConsignorGln = formValue.originalConsignorGln;
            updatedNote.originalConsignor = formValue.originalConsignor;
            updatedNote.showDateAnnouncedTime = hasTimeProvided;

            if (!updatedNote.properties) {
                updatedNote.properties = {};
            }
            updatedNote.properties.referenceNumber =
                formValue.referenceNumber || '';

            this.dialogRef.close(updatedNote);
        } catch (error: any) {
            console.error('Error updating delivery note:', error);
            this.toastr.error(this.translate.instant('SHARED.ERRORS.GENERAL'));
        } finally {
            this.loading.set(false);
        }
    }

    get formControls(): { [p: string]: AbstractControl } {
        return this.patchDeliveryNoteForm.controls;
    }
}
